import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { SelectionPermission } from 'store/productManagement/reducer.types';
import useNetworkTarget from 'customHooks/useNetworkTarget';
import { changeNetworkAndPricingMode, hideSecondaryPanel } from 'store/productManagement/reducer';
import { getPublisherTargetCriteria } from 'store/publisher/selectors';
import { NetworkReferenceOptions } from 'store/userOptions/reducer.types';
import { Label } from 'lib/Label';
import { CodeNameModel } from 'components/common/types';
import NetworkOptions from 'components/common/NetworkOptions';
import { Store } from 'components/common/types/Store.types';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import Search from 'lib/Search';
import { definedRangePermissionOptions as permissionOptions } from 'components/pages/PCM/Products/consts';
import { transformAvailableNetworks } from 'utils/transformAvailableNetworks';
import { isValueRequired } from '../../utils/isValueRequired';
import { isPanelConfirmDisabled } from '../../utils/isPanelConfirmDisabled';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { ProductNetworksPanelProps } from './ProductNetworksPanel.types';
import { secondaryPanelLayoutStyles } from '../../styles';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const ProductNetworksPanel: React.FC<ProductNetworksPanelProps> = ({
  transformedNetworks,
  fieldName,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch();
  const networkCategoryOptions = useSelector((state: Store) => state.userOptions.networks);
  const availableNetworkFrames = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.availability.availableNetworkFrames,
  );
  const productNetworks = useSelector((state: Store) => state.productManagement.newProduct.networks);
  const savedNetworks = productNetworks?.value ?? [];
  const defaultSelectedPermission =
    permissionOptions.find(({ code }) => code === productNetworks?.selectionPermission) ?? permissionOptions[0];
  const [selectedPermission, setSelectedPermission] = useState<{
    code: SelectionPermission;
    name: string;
  }>(defaultSelectedPermission);
  const [availableNetworks, setAvailableNetworks] = useState<NetworkReferenceOptions>({});
  const defaultCriteria = useSelector(getPublisherTargetCriteria);

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    isOptionSelected,
    onToggleCategory,
    setSelectedOptions,
  } = useCategoryOptions({
    options: availableNetworks as Record<string, CodeNameModel[]>,
    initialSelectedOptions: transformedNetworks as CodeNameModel[],
  });

  const mappedOptions = useMemo(() => {
    return selectedOptions.map((option: NetworkCriteriaType) => ({
      ...option,
    }));
  }, [selectedOptions]);

  const { isToleranceError, isChange, isChangeValid, isRangeValuesError, ...networkTarget } = useNetworkTarget({
    selectedOptions: mappedOptions,
    setSelectedOptions,
    savedNetworks,
  });

  const hasNetworksPanelChanges = isChange || selectedPermission.code !== productNetworks?.selectionPermission;

  useEffect(() => {
    if (!networkCategoryOptions?.length) return;

    setAvailableNetworks(
      transformAvailableNetworks({
        availableNetworks: networkCategoryOptions,
        networksAvailability: availableNetworkFrames,
        defaultCriteria,
      }),
    );
  }, [networkCategoryOptions, defaultCriteria]);

  const onConfirm = (networksToConfirm: NetworkCriteriaType[]): void => {
    dispatch(changeNetworkAndPricingMode({ fieldName, value: networksToConfirm, selectedPermission }));
    dispatch(hideSecondaryPanel());
  };

  const isRange = selectedPermission.code === SelectionPermission.RANGE_VALUE;
  const isRangeError = isRange && isRangeValuesError;

  return (
    <ProductSecondaryPanel
      title="Networks"
      isDisabled={
        isToleranceError ||
        !isChangeValid ||
        isPanelConfirmDisabled({
          field: productNetworks,
          selectedPermission,
          newFieldValue: mappedOptions.length ? mappedOptions : undefined,
        }) ||
        isRangeError ||
        isReadOnly
      }
      onSubmit={() => onConfirm(selectedOptions as NetworkCriteriaType[])}
      onCancel={() => dispatch(hideSecondaryPanel())}
      customStyles={secondaryPanelLayoutStyles}
      selectedPermission={selectedPermission.code}
      isChange={hasNetworksPanelChanges}
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
        permissionOptions={permissionOptions}
      />
      {isValueRequired(selectedPermission.code) && (
        <>
          <div className="my-4">
            <SectionDivider />
          </div>

          <Label label="Select networks" />

          <div className="mb-2">
            <Search value={searchText} placeholder="Search networks" onChange={onFilterTextChange} />
          </div>

          {searchText?.length >= 3 && isEmpty(filteredOptions) ? (
            <p>There are no results matching your search</p>
          ) : null}

          {!!filteredOptions && (
            <NetworkOptions
              options={filteredOptions}
              selectedOptions={selectedOptions}
              onCategoryDirectionSort={onCategoryDirectionSort}
              setSelectedOptions={setSelectedOptions}
              openCategories={openCategories}
              sortDirection={sortDirection}
              onToggleCategory={onToggleCategory}
              isOptionSelected={isOptionSelected}
              getNumberOfFilteredOptions={getNumberOfFilteredOptions}
              networkTarget={{ isToleranceError, isChange, isChangeValid, ...networkTarget }}
              isRange={isRange}
            />
          )}
        </>
      )}
    </ProductSecondaryPanel>
  );
};

export default ProductNetworksPanel;
